import { useQuery } from '@tanstack/preact-query';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';
import { ApiClientContext } from '../arcticapi';
import type { PlaceSearchResult, Stop } from '../arcticapi/types';
import type { MapViewport } from '../components/base/Map/Map';
import { useDebouncedValue } from './useDebouncedValue';

const MAP_BOUNDS_DEBOUNCE_MS = 250;
const MIN_STOP_SEARCH_ZOOM = 13;

export interface LiveDeparturesMapState {
  selectedLocation: PlaceSearchResult | null;
  onViewportChange: (viewport: MapViewport) => void;
  canSearchStops: boolean;
  nearbyStops: Stop[];
  nearbyStopsLoading: boolean;
  nearbyStopsError: Error | null;
}

interface UseLiveDeparturesMapOptions {
  selectedLocation: PlaceSearchResult | null;
  preserveBounds?: boolean;
}

export function useLiveDeparturesMap({
  selectedLocation,
  preserveBounds = false,
}: UseLiveDeparturesMapOptions): LiveDeparturesMapState {
  const client = useContext(ApiClientContext);
  const [mapViewport, setMapViewport] = useState<MapViewport | null>(null);
  const debouncedMapViewport = useDebouncedValue(
    mapViewport,
    MAP_BOUNDS_DEBOUNCE_MS,
  );
  const canSearchStops =
    selectedLocation !== null &&
    mapViewport !== null &&
    mapViewport.zoom >= MIN_STOP_SEARCH_ZOOM;
  const isViewportUpdatePending = mapViewport !== debouncedMapViewport;

  useEffect(() => {
    if (!selectedLocation || preserveBounds) {
      return;
    }

    setMapViewport(null);
  }, [preserveBounds, selectedLocation]);

  const nearbyStopsQuery = useQuery<Stop[], Error>({
    queryKey: [
      'nearby-stops',
      selectedLocation?.id,
      debouncedMapViewport?.bounds[0],
      debouncedMapViewport?.bounds[1],
    ],
    enabled:
      Boolean(client) &&
      selectedLocation !== null &&
      canSearchStops &&
      debouncedMapViewport !== null &&
      debouncedMapViewport.zoom >= MIN_STOP_SEARCH_ZOOM,
    placeholderData: (previousStops) => previousStops,
    queryFn: ({ signal }) => {
      if (!client || !debouncedMapViewport) {
        throw new Error('Stops request is missing map bounds.');
      }

      return client.searchStops(
        {
          bounds: debouncedMapViewport.bounds,
        },
        { signal },
      );
    },
  });

  const handleMapViewportChange = useCallback((nextViewport: MapViewport) => {
    setMapViewport((previousViewport) => {
      if (
        previousViewport &&
        previousViewport.zoom === nextViewport.zoom &&
        previousViewport.bounds[0] === nextViewport.bounds[0] &&
        previousViewport.bounds[1] === nextViewport.bounds[1]
      ) {
        return previousViewport;
      }

      return nextViewport;
    });
  }, []);

  return {
    selectedLocation,
    onViewportChange: handleMapViewportChange,
    canSearchStops,
    nearbyStops: canSearchStops ? (nearbyStopsQuery.data ?? []) : [],
    nearbyStopsLoading:
      Boolean(client) &&
      canSearchStops &&
      (nearbyStopsQuery.isFetching || isViewportUpdatePending),
    nearbyStopsError: canSearchStops ? nearbyStopsQuery.error : null,
  };
}
